import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { motion, useInView, useAnimation } from "framer-motion";
import { 
  Database, 
  Users, 
  Building2, 
  Shield, 
  Zap, 
  Globe, 
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Animated counter component
const AnimatedCounter = ({ target, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(target);
      const incrementTime = (duration * 1000) / end;
      
      const timer = setInterval(() => {
        start += Math.ceil(end / 50);
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, incrementTime);

      return () => clearInterval(timer);
    }
  }, [isInView, target, duration]);

  return <span ref={ref} className="stat-number">{count}{suffix}</span>;
};

// Section animation wrapper
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.6, delay, ease: "easeOut" }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="logo-text text-xl md:text-2xl text-white" data-testid="logo">
            Structurata
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="/#features" className="text-muted-foreground hover:text-white transition-colors duration-200">
              Features
            </a>
            <a href="/#data" className="text-muted-foreground hover:text-white transition-colors duration-200">
              Data
            </a>
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white transition-colors duration-200">
              Contact
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-muted-foreground hover:text-white hover:bg-white/5"
              data-testid="sign-in-btn"
              onClick={() => navigate('/signin')}
            >
              Sign In
            </Button>
            <Button 
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 glow-blue glow-blue-hover btn-shine"
              data-testid="get-started-btn"
              onClick={() => window.location.href = 'mailto:sales@structurata.com'}
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass border-t border-white/5 px-6 py-6"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-4">
            <a href="/#features" className="text-muted-foreground hover:text-white py-2">Features</a>
            <a href="/#data" className="text-muted-foreground hover:text-white py-2">Data</a>
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white py-2">Contact</a>
            <hr className="border-white/10" />
            <Button 
              variant="ghost" 
              className="justify-start text-muted-foreground hover:text-white"
              onClick={() => navigate('/signin')}
            >
              Sign In
            </Button>
            <Button 
              className="bg-primary hover:bg-primary/90 text-white rounded-full"
              onClick={() => window.location.href = 'mailto:sales@structurata.com'}
            >
              Get Started
            </Button>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen pt-32 pb-24 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 grid-bg" />
      <div className="glow-orb glow-orb-blue w-[600px] h-[600px] -top-40 -right-40" />
      <div className="glow-orb glow-orb-purple w-[400px] h-[400px] top-1/2 -left-40" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary mb-6">
                <Database size={14} />
                Data Infrastructure
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]"
              data-testid="hero-title"
            >
              The Infrastructure for{" "}
              <span className="text-gradient">Global Data</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-lg"
              data-testid="hero-description"
            >
              Access the most comprehensive person and organization datasets. 
              Built for enrichment platforms, data teams, and AI-driven workflows 
              that demand accuracy at scale.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg font-medium glow-blue glow-blue-hover btn-shine"
                data-testid="hero-cta-primary"
                onClick={() => window.location.href = 'mailto:sales@structurata.com'}
              >
                Start Free
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white/20 hover:bg-white/5 text-white rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm"
                data-testid="hero-cta-secondary"
                onClick={() => window.location.href = 'mailto:sales@structurata.com'}
              >
                View Datasets
              </Button>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex items-center gap-6 pt-8"
            >
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 size={16} className="text-secondary" />
                <span>Enterprise ready</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 size={16} className="text-secondary" />
                <span>Global coverage</span>
              </div>
            </motion.div>
          </div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/10 glass-card">
              <img 
                src="https://images.unsplash.com/photo-1639322537228-f710d846310a?w=800&q=80"
                alt="Data visualization"
                className="w-full h-[500px] object-cover pulse-slow opacity-80"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              
              {/* Floating data cards */}
              <div className="absolute bottom-8 left-8 right-8 glass rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Users size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Person Records</p>
                      <p className="text-lg font-semibold text-white">850M+</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                      <Building2 size={20} className="text-secondary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Organizations</p>
                      <p className="text-lg font-semibold text-white">100M+</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Trusted By Section
const TrustedBySection = () => {
  const companies = [
    "DataFlow", "TechCorp", "GlobalEnrich", "ScaleAI", "CloudData", "InfoSys"
  ];

  return (
    <section data-testid="trusted-by-section" className="py-16 border-y border-white/5 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-center text-sm font-mono uppercase tracking-widest text-muted-foreground mb-10">
          Trusted by leading data teams
        </p>
        <div className="flex justify-center items-center gap-12 md:gap-20 flex-wrap opacity-50">
          {companies.map((company, index) => (
            <span 
              key={index} 
              className="text-xl md:text-2xl font-semibold text-white/60 font-unbounded tracking-tight"
            >
              {company}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: "850", suffix: "M+", label: "Person Records", icon: Users },
    { value: "100", suffix: "M+", label: "Organization Profiles", icon: Building2 },
    { value: "99", suffix: "%", label: "Data Accuracy", icon: Shield },
    { value: "2.5", suffix: " weeks", label: "Refresh Cycle", icon: Zap },
  ];

  return (
    <section data-testid="stats-section" id="data" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="glow-orb glow-orb-green w-[500px] h-[500px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary mb-4">
            <Database size={14} />
            Verified Data Points
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white">
            Billions of records,{" "}
            <span className="text-gradient">refreshed constantly</span>
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <AnimatedSection key={index} delay={index * 0.1}>
              <div 
                className="glass-card border border-white/10 rounded-2xl p-6 md:p-8 text-center group hover:border-primary/30 transition-colors duration-300"
                data-testid={`stat-card-${index}`}
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <stat.icon size={24} className="text-primary" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-white mb-2">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: Users,
      title: "Person Data",
      description: "Comprehensive individual profiles with verified contact information, professional history, and social connections.",
      highlight: "850M+ profiles"
    },
    {
      icon: Building2,
      title: "Organization Data",
      description: "Detailed company records including firmographics, tech stack, funding history, and key decision makers.",
      highlight: "100M+ companies"
    },
    {
      icon: Globe,
      title: "Global Coverage",
      description: "Data spanning 190+ countries with regional accuracy and compliance with local data regulations.",
      highlight: "190+ countries"
    },
    {
      icon: Zap,
      title: "Real-time Updates",
      description: "Continuously refreshed datasets ensure you're always working with the most current information available.",
      highlight: "2.5 week refresh"
    },
    {
      icon: Shield,
      title: "Data Quality",
      description: "Multi-layer verification process ensures industry-leading accuracy rates across all data points.",
      highlight: "99% accuracy"
    },
    {
      icon: Database,
      title: "Bulk Exports",
      description: "Download complete datasets in multiple formats. Perfect for enrichment, ML training, and analytics.",
      highlight: "CSV, JSON, Parquet"
    },
  ];

  return (
    <section data-testid="features-section" id="features" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-primary mb-4">
            <Zap size={14} />
            Capabilities
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4">
            Everything you need for{" "}
            <span className="text-gradient">data excellence</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Purpose-built datasets that power the world's leading enrichment platforms, 
            sales intelligence tools, and AI applications.
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <AnimatedSection key={index} delay={index * 0.1}>
              <div 
                className="feature-card glass-card border border-white/10 rounded-2xl p-8 group hover:border-primary/30 transition-colors duration-300 h-full"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 mb-6 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <feature.icon size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-muted-foreground mb-4 leading-relaxed">{feature.description}</p>
                <span className="inline-block text-sm font-mono text-secondary">{feature.highlight}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Sign In Page
const SignInPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    // Simulate API call delay
    setTimeout(() => {
      setIsLoading(false);
      setError("Incorrect password. Please try again.");
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="glow-orb glow-orb-blue w-[400px] h-[400px] top-1/4 -left-20" />
      <div className="glow-orb glow-orb-purple w-[300px] h-[300px] bottom-1/4 -right-20" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="glass-card border border-white/10 rounded-2xl p-8">
          {/* Logo */}
          <a href="/" className="logo-text text-2xl text-white block text-center mb-8">
            Structurata
          </a>

          <h1 className="text-2xl font-semibold text-white text-center mb-2">
            Welcome back
          </h1>
          <p className="text-muted-foreground text-center mb-8">
            Sign in to access your account
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-muted-foreground focus:border-primary"
                data-testid="signin-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-muted-foreground focus:border-primary"
                data-testid="signin-password-input"
              />
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg p-3"
                data-testid="signin-error"
              >
                <AlertCircle size={16} />
                {error}
              </motion.div>
            )}

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6 font-medium glow-blue glow-blue-hover"
              disabled={isLoading}
              data-testid="signin-submit-btn"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a 
              href="mailto:support@structurata.com" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Forgot your password? Contact support
            </a>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <a 
                href="mailto:sales@structurata.com" 
                className="text-primary hover:underline"
              >
                Contact sales
              </a>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="glow-orb glow-orb-blue w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      
      <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
        <AnimatedSection>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Ready to build with{" "}
            <span className="text-gradient">world-class data?</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
            Start exploring our datasets today and see why 
            leading data teams trust Structurata for their most critical workflows.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-6 text-lg font-medium glow-blue glow-blue-hover btn-shine"
              data-testid="cta-primary-btn"
              onClick={() => window.location.href = 'mailto:sales@structurata.com'}
            >
              Get Started Free
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white/20 hover:bg-white/5 text-white rounded-full px-10 py-6 text-lg font-medium backdrop-blur-sm"
              data-testid="cta-secondary-btn"
              onClick={() => window.location.href = 'mailto:sales@structurata.com'}
            >
              Talk to Sales
            </Button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const footerLinks = {
    product: [
      { label: "Datasets", href: "mailto:sales@structurata.com" },
      { label: "Person Data", href: "mailto:sales@structurata.com" },
      { label: "Organization Data", href: "mailto:sales@structurata.com" },
      { label: "Pricing", href: "mailto:sales@structurata.com" },
    ],
    company: [
      { label: "About", href: "mailto:support@structurata.com" },
      { label: "Blog", href: "mailto:support@structurata.com" },
      { label: "Careers", href: "mailto:support@structurata.com" },
      { label: "Contact", href: "mailto:support@structurata.com" },
    ],
    resources: [
      { label: "Documentation", href: "mailto:support@structurata.com" },
      { label: "API Reference", href: "mailto:support@structurata.com" },
      { label: "Status", href: "mailto:support@structurata.com" },
      { label: "Changelog", href: "mailto:support@structurata.com" },
    ],
    legal: [
      { label: "Privacy", href: "mailto:support@structurata.com" },
      { label: "Terms", href: "mailto:support@structurata.com" },
      { label: "Security", href: "mailto:support@structurata.com" },
      { label: "Compliance", href: "mailto:support@structurata.com" },
    ],
  };

  return (
    <footer data-testid="footer" className="py-16 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <a href="/" className="logo-text text-xl text-white mb-4 block">
              Structurata
            </a>
            <p className="text-sm text-muted-foreground">
              The infrastructure for global person and organization data.
            </p>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                {category}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <a 
                      href={link.href}
                      className="text-sm text-muted-foreground hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © 2025 Structurata. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white transition-colors duration-200">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white transition-colors duration-200">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </a>
            <a href="mailto:support@structurata.com" className="text-muted-foreground hover:text-white transition-colors duration-200">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground noise">
      <Navbar />
      <main>
        <HeroSection />
        <TrustedBySection />
        <StatsSection />
        <FeaturesSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

// Main App Component with Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signin" element={<SignInPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
